import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { reconcileSumup, SumupSummary } from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload one sales report per month (sales-report-YYYY-MM-DD_YYYY-MM-DD.xlsx).',
  'Upload the transactions reports covering those months. Include the month before the first sales month so carry-over payouts can be deducted.',
  'Optionally upload a VAT product list; sales lines are checked against it.',
  'Click Run Reconciliation. Each sales month gets its own block in the downloaded workbook.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

type Row = Record<string, string | number | null>;

function MiniTable({ rows }: { rows: Row[] }) {
  if (rows.length === 0) return null;
  const columns = Object.keys(rows[0]);
  return (
    <div className="mt-2 overflow-x-auto rounded-md border border-border bg-background">
      <table className="w-full text-left text-xs">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            {columns.map((c) => (
              <th key={c} className="px-3 py-1.5 font-semibold">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-border">
              {columns.map((c) => (
                <td key={c} className="px-3 py-1.5 text-foreground">
                  {row[c] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SumupReconPanel({ mission, onBack }: PanelProps) {
  const [salesFiles, setSalesFiles] = useState<File[]>([]);
  const [txnFiles, setTxnFiles] = useState<File[]>([]);
  const [vatFiles, setVatFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<SumupSummary>>();

  const handleSubmit = async () => {
    if (salesFiles.length === 0 || txnFiles.length === 0) {
      fail('Upload at least one sales report and one transactions report.');
      return;
    }
    await run(() => reconcileSumup(salesFiles, txnFiles, vatFiles[0] ?? null), 'Reconciliation failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <FileDrop
        label="Sales report(s)"
        note="One report per month. Select several at once to reconcile several months."
        required
        multiple
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={salesFiles}
        onFiles={setSalesFiles}
      />
      <FileDrop
        label="Transactions report(s)"
        note="Add the previous month's report too, to capture carry-over payouts."
        required
        multiple
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={txnFiles}
        onFiles={setTxnFiles}
      />
      <FileDrop
        label="VAT product list (optional)"
        note="Products with their VAT % (rates as column headers, e.g. 0%, 5%, 20%). Without it, the default hot-drink rule is used."
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={vatFiles}
        onFiles={setVatFiles}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Reconciling…' : 'Run Reconciliation'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Reconciling…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            ✅ {result.filename} downloaded{s ? ` (${s.month})` : ''}.
          </div>
          {s?.messages.map((m, i) => (
            <div
              key={i}
              className={`rounded-lg px-4 py-3 ${
                m.level === 'success' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'
              }`}
            >
              {m.text}
            </div>
          ))}
          {s && s.vat_duplicate_count > 0 && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
              ⚠️ {s.vat_duplicate_count} product(s) have more than one VAT rate in the VAT list, so
              their sales lines can't be validated until the list is fixed.
              <MiniTable rows={s.vat_duplicates} />
            </div>
          )}
          {s && s.vat_mismatch_lines > 0 && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
              ❌ {s.vat_mismatch_lines} sales line(s) have a VAT % that differs from the VAT list
              (recalculated using the list rate).
              <MiniTable rows={s.vat_mismatch} />
            </div>
          )}
          {s && s.vat_not_in_list_lines > 0 && (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              {s.vat_not_in_list_lines} sales line(s) are for products not in the VAT list (default
              hot-drink rule applied).
              <MiniTable rows={s.vat_not_in_list} />
            </div>
          )}
          {s && s.vat_checked && s.vat_duplicate_count === 0 && s.vat_mismatch_lines === 0 && (
            <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
              ✅ VAT % in the sales reports matches the VAT list.
            </div>
          )}
          {s && s.log.length > 0 && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">📋 Processing log</summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                {s.log.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && (
        <InfoNotice>📁 Upload the sales report and at least one transactions report.</InfoNotice>
      )}
    </MissionShell>
  );
}

export default SumupReconPanel;
